// Compact annotation-list editor shared by the ontology class/property
// editors and the vocabulary concept editor: one row per annotation
// statement (predicate select + value + remove), with an add-row button.
// The list is a REPLACE-SET on save — the caller round-trips exactly what
// it read, so removing a row deletes that statement.

import { ANNOTATION_PREDICATE_LABELS, type AnnotationValue } from '@/lib/useOntology';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';

// No width here — the select and textarea size themselves (a `w-full` in a
// shared class fought the select's `w-40` and crushed the textarea to zero
// width, making annotation values impossible to see or edit).
const rowInputClass =
  'rounded-md border border-border px-2 py-1.5 text-sm text-tres-text-primary ' +
  'placeholder:text-tres-text-secondary focus:border-tres-action focus:outline-none ' +
  'focus:ring-1 focus:ring-tres-action disabled:bg-muted';

export function AnnotationListEditor({
  value,
  onChange,
  predicates,
  disabled,
  label = 'Annotations',
}: {
  value: AnnotationValue[];
  onChange: (next: AnnotationValue[]) => void;
  /** The managed predicate set this editor offers (term vs concept). */
  predicates: readonly string[];
  disabled: boolean;
  label?: string;
}) {
  function update(idx: number, patch: Partial<AnnotationValue>) {
    onChange(value.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }
  function remove(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
  }
  function add() {
    const first = predicates[0] ?? ':comment';
    onChange([...value, { predicate: first, value: '' }]);
  }

  return (
    <div className="mb-3">
      <span className="text-sm font-medium text-tres-text-primary">{label}</span>
      <div className="mt-1 space-y-1.5">
        {value.length === 0 && (
          <p className="text-xs italic text-tres-text-secondary">
            No annotations. Add a definition, comment, or scope note.
          </p>
        )}
        {value.map((a, idx) => (
          <div key={idx} className="flex items-start gap-1.5">
            <select
              value={a.predicate}
              onChange={(e) => update(idx, { predicate: e.target.value })}
              disabled={disabled}
              className={`${rowInputClass} w-40 shrink-0`}
              aria-label="Annotation kind"
            >
              {predicates.map((p) => (
                <option key={p} value={p}>
                  {ANNOTATION_PREDICATE_LABELS[p] ?? p}
                </option>
              ))}
            </select>
            <textarea
              value={a.value}
              onChange={(e) => update(idx, { value: e.target.value })}
              disabled={disabled}
              rows={a.value.length > 80 ? 3 : 1}
              maxLength={2000}
              className={`${rowInputClass} min-w-0 flex-1`}
              placeholder="Value"
            />
            {a.lang && (
              <span
                className="mt-2 shrink-0 text-xs text-tres-text-secondary"
                title="Language tag (preserved on save)"
              >
                @{a.lang}
              </span>
            )}
            <button
              type="button"
              onClick={() => remove(idx)}
              disabled={disabled}
              className={cn(buttonVariants({ variant: 'ghost', size: 'icon' }), 'mt-1 shrink-0')}
              title="Remove this annotation"
              aria-label="Remove this annotation"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'border-dashed text-tres-text-secondary')}
        >
          + Add annotation
        </button>
      </div>
    </div>
  );
}
